// Mock data for Curriculum Planning page

export interface CurriculumPlan {
  id: string;
  name: string;
  academicYear: string;
  subjects: SubjectAllocation[];
  totalWeeks: number;
  cycleType: 'weekly' | 'bi-weekly';
  status: 'draft' | 'approved' | 'active';
}

export interface SubjectAllocation {
  id: string;
  subjectId: string;
  subjectName: string;
  subjectCode: string;
  yearGroups: YearGroupAllocation[];
  totalPeriodsPerWeek: number;
  lessonDuration: number;
  requiresDoubleLesson: boolean;
  department: string;
}

export interface YearGroupAllocation {
  yearGroup: string;
  periodsPerWeek: number;
  bands: string[];
  prerequisites: string[];
  isCore: boolean;
}

export interface Band {
  id: string;
  name: string;
  type: 'ability' | 'mixed' | 'non-ability';
  yearGroup: string;
  capacity: number;
  subjects: string[];
  description: string;
}

export interface YearGroup {
  id: string;
  name: string;
  bands: Band[];
  totalStudents: number;
}

export interface SubjectSet {
  id: string;
  subjectName: string;
  yearGroup: string;
  bandName: string;
  setCount: number;
  parallelTeaching: boolean;
  abilityLevel: 'high' | 'medium' | 'low' | 'mixed';
}

export const mockData = {
  curriculumPlan: {
    id: 'CP001',
    name: 'Academic Year 2024-2025 Curriculum',
    academicYear: '2024-2025',
    totalWeeks: 39,
    cycleType: 'bi-weekly' as const,
    status: 'draft' as const,
    subjects: [
      {
        id: 'SA001',
        subjectId: 'MATH',
        subjectName: 'Mathematics',
        subjectCode: 'MATH',
        totalPeriodsPerWeek: 25,
        lessonDuration: 60,
        requiresDoubleLesson: false,
        department: 'Mathematics',
        yearGroups: [
          {
            yearGroup: 'Year 7',
            periodsPerWeek: 5,
            bands: ['High', 'Middle', 'Foundation'],
            prerequisites: [],
            isCore: true,
          },
          {
            yearGroup: 'Year 8',
            periodsPerWeek: 5,
            bands: ['High', 'Middle', 'Foundation'],
            prerequisites: ['Year 7 Mathematics'],
            isCore: true,
          },
          {
            yearGroup: 'Year 9',
            periodsPerWeek: 5,
            bands: ['High', 'Middle', 'Foundation'],
            prerequisites: ['Year 8 Mathematics'],
            isCore: true,
          },
          {
            yearGroup: 'Year 10',
            periodsPerWeek: 5,
            bands: ['High', 'Middle', 'Foundation'],
            prerequisites: ['Year 9 Mathematics'],
            isCore: true,
          },
          {
            yearGroup: 'Year 11',
            periodsPerWeek: 5,
            bands: ['High', 'Middle', 'Foundation'],
            prerequisites: ['Year 10 Mathematics'],
            isCore: true,
          },
        ],
      },
      {
        id: 'SA002',
        subjectId: 'ENG',
        subjectName: 'English Literature',
        subjectCode: 'ENG',
        totalPeriodsPerWeek: 20,
        lessonDuration: 60,
        requiresDoubleLesson: true,
        department: 'English',
        yearGroups: [
          {
            yearGroup: 'Year 7',
            periodsPerWeek: 4,
            bands: ['Mixed'],
            prerequisites: [],
            isCore: true,
          },
          {
            yearGroup: 'Year 8',
            periodsPerWeek: 4,
            bands: ['Mixed'],
            prerequisites: ['Year 7 English'],
            isCore: true,
          },
          {
            yearGroup: 'Year 9',
            periodsPerWeek: 4,
            bands: ['Mixed'],
            prerequisites: ['Year 8 English'],
            isCore: true,
          },
          {
            yearGroup: 'Year 10',
            periodsPerWeek: 4,
            bands: ['High', 'Standard'],
            prerequisites: ['Year 9 English'],
            isCore: true,
          },
          {
            yearGroup: 'Year 11',
            periodsPerWeek: 4,
            bands: ['High', 'Standard'],
            prerequisites: ['Year 10 English'],
            isCore: true,
          },
        ],
      },
      {
        id: 'SA003',
        subjectId: 'SCI',
        subjectName: 'Science',
        subjectCode: 'SCI',
        totalPeriodsPerWeek: 18,
        lessonDuration: 60,
        requiresDoubleLesson: true,
        department: 'Science',
        yearGroups: [
          {
            yearGroup: 'Year 7',
            periodsPerWeek: 3,
            bands: ['Mixed'],
            prerequisites: [],
            isCore: true,
          },
          {
            yearGroup: 'Year 8',
            periodsPerWeek: 3,
            bands: ['Mixed'],
            prerequisites: ['Year 7 Science'],
            isCore: true,
          },
          {
            yearGroup: 'Year 9',
            periodsPerWeek: 4,
            bands: ['Mixed'],
            prerequisites: ['Year 8 Science'],
            isCore: true,
          },
          {
            yearGroup: 'Year 10',
            periodsPerWeek: 4,
            bands: ['Triple', 'Double'],
            prerequisites: ['Year 9 Science'],
            isCore: true,
          },
          {
            yearGroup: 'Year 11',
            periodsPerWeek: 4,
            bands: ['Triple', 'Double'],
            prerequisites: ['Year 10 Science'],
            isCore: true,
          },
        ],
      },
      {
        id: 'SA004',
        subjectId: 'HIST',
        subjectName: 'History',
        subjectCode: 'HIST',
        totalPeriodsPerWeek: 12,
        lessonDuration: 60,
        requiresDoubleLesson: false,
        department: 'Humanities',
        yearGroups: [
          {
            yearGroup: 'Year 7',
            periodsPerWeek: 2,
            bands: ['Mixed'],
            prerequisites: [],
            isCore: false,
          },
          {
            yearGroup: 'Year 8',
            periodsPerWeek: 2,
            bands: ['Mixed'],
            prerequisites: [],
            isCore: false,
          },
          {
            yearGroup: 'Year 9',
            periodsPerWeek: 2,
            bands: ['Mixed'],
            prerequisites: [],
            isCore: false,
          },
          {
            yearGroup: 'Year 10',
            periodsPerWeek: 3,
            bands: ['Standard'],
            prerequisites: [],
            isCore: false,
          },
          {
            yearGroup: 'Year 11',
            periodsPerWeek: 3,
            bands: ['Standard'],
            prerequisites: ['Year 10 History'],
            isCore: false,
          },
        ],
      },
      {
        id: 'SA005',
        subjectId: 'FREN',
        subjectName: 'French',
        subjectCode: 'FREN',
        totalPeriodsPerWeek: 9,
        lessonDuration: 60,
        requiresDoubleLesson: false,
        department: 'Modern Foreign Languages',
        yearGroups: [
          {
            yearGroup: 'Year 7',
            periodsPerWeek: 2,
            bands: ['Mixed'],
            prerequisites: [],
            isCore: false,
          },
          {
            yearGroup: 'Year 8',
            periodsPerWeek: 2,
            bands: ['Mixed'],
            prerequisites: ['Year 7 French'],
            isCore: false,
          },
          {
            yearGroup: 'Year 9',
            periodsPerWeek: 2,
            bands: ['Mixed'],
            prerequisites: ['Year 8 French'],
            isCore: false,
          },
          {
            yearGroup: 'Year 10',
            periodsPerWeek: 3,
            bands: ['Standard'],
            prerequisites: ['Year 9 French'],
            isCore: false,
          },
        ],
      },
    ],
  } as CurriculumPlan,

  yearGroups: [
    {
      id: 'YG7',
      name: 'Year 7',
      totalStudents: 180,
      bands: [
        {
          id: 'B701',
          name: 'High Ability Band',
          type: 'ability' as const,
          yearGroup: 'Year 7',
          capacity: 60,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'High achieving students across core subjects',
        },
        {
          id: 'B702',
          name: 'Middle Ability Band',
          type: 'ability' as const,
          yearGroup: 'Year 7',
          capacity: 90,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'Average achieving students with mixed ability grouping',
        },
        {
          id: 'B703',
          name: 'Foundation Band',
          type: 'ability' as const,
          yearGroup: 'Year 7',
          capacity: 30,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'Students requiring additional support',
        },
      ],
    },
    {
      id: 'YG8',
      name: 'Year 8',
      totalStudents: 175,
      bands: [
        {
          id: 'B801',
          name: 'High Ability Band',
          type: 'ability' as const,
          yearGroup: 'Year 8',
          capacity: 55,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'High achieving students across core subjects',
        },
        {
          id: 'B802',
          name: 'Middle Ability Band',
          type: 'ability' as const,
          yearGroup: 'Year 8',
          capacity: 90,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'Average achieving students with mixed ability grouping',
        },
        {
          id: 'B803',
          name: 'Foundation Band',
          type: 'ability' as const,
          yearGroup: 'Year 8',
          capacity: 30,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'Students requiring additional support',
        },
      ],
    },
    {
      id: 'YG9',
      name: 'Year 9',
      totalStudents: 165,
      bands: [
        {
          id: 'B901',
          name: 'High Ability Band',
          type: 'ability' as const,
          yearGroup: 'Year 9',
          capacity: 50,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'High achieving students preparing for GCSE options',
        },
        {
          id: 'B902',
          name: 'Middle Ability Band',
          type: 'ability' as const,
          yearGroup: 'Year 9',
          capacity: 85,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'Average achieving students with mixed ability grouping',
        },
        {
          id: 'B903',
          name: 'Foundation Band',
          type: 'ability' as const,
          yearGroup: 'Year 9',
          capacity: 30,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'Students requiring additional support',
        },
      ],
    },
    {
      id: 'YG10',
      name: 'Year 10',
      totalStudents: 160,
      bands: [
        {
          id: 'B1001',
          name: 'Higher Tier Band',
          type: 'ability' as const,
          yearGroup: 'Year 10',
          capacity: 80,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'Students taking higher tier GCSEs',
        },
        {
          id: 'B1002',
          name: 'Foundation Tier Band',
          type: 'ability' as const,
          yearGroup: 'Year 10',
          capacity: 80,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'Students taking foundation tier GCSEs',
        },
      ],
    },
    {
      id: 'YG11',
      name: 'Year 11',
      totalStudents: 155,
      bands: [
        {
          id: 'B1101',
          name: 'Higher Tier Band',
          type: 'ability' as const,
          yearGroup: 'Year 11',
          capacity: 75,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'Students taking higher tier GCSEs',
        },
        {
          id: 'B1102',
          name: 'Foundation Tier Band',
          type: 'ability' as const,
          yearGroup: 'Year 11',
          capacity: 80,
          subjects: ['Mathematics', 'English', 'Science'],
          description: 'Students taking foundation tier GCSEs',
        },
      ],
    },
  ] as YearGroup[],

  subjectSets: [
    {
      id: 'SS001',
      subjectName: 'Mathematics',
      yearGroup: 'Year 10',
      bandName: 'Higher Tier Band',
      setCount: 3,
      parallelTeaching: true,
      abilityLevel: 'high' as const,
    },
    {
      id: 'SS002',
      subjectName: 'Mathematics',
      yearGroup: 'Year 10',
      bandName: 'Foundation Tier Band',
      setCount: 3,
      parallelTeaching: true,
      abilityLevel: 'medium' as const,
    },
    {
      id: 'SS003',
      subjectName: 'English Literature',
      yearGroup: 'Year 11',
      bandName: 'Higher Tier Band',
      setCount: 2,
      parallelTeaching: false,
      abilityLevel: 'high' as const,
    },
    {
      id: 'SS004',
      subjectName: 'Science',
      yearGroup: 'Year 9',
      bandName: 'High Ability Band',
      setCount: 2,
      parallelTeaching: true,
      abilityLevel: 'high' as const,
    },
    {
      id: 'SS005',
      subjectName: 'Science',
      yearGroup: 'Year 9',
      bandName: 'Middle Ability Band',
      setCount: 3,
      parallelTeaching: true,
      abilityLevel: 'medium' as const,
    },
  ] as SubjectSet[],
};
